import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { encodeCanvas } from '../shared/imageOutputFormats';
import OutputFormatPicker from '../shared/OutputFormatPicker';
import { toDisplayableBlob } from '../shared/heicDisplay';
import {
    IconArrowLeft,
    IconArrowsMaximize,
    IconArrowsMinimize,
    IconDownload,
    IconEye,
    IconFileTypeJpg,
    IconFileTypePng,
    IconMinus,
    IconPhoto,
    IconPhotoScan,
    IconPlus,
    IconPrinter,
    IconRestore,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import useImagePaste from '../../components/image/useImagePaste';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import Hint from '../../../../../components/common/Hint';
import ImagePreviewModal from '../../../../../components/common/ImagePreviewModal';
import {
    downloadBlob,
    deriveToolStatus,
    getToolStatusText,
} from '../../toolPageUtils';
import {
    applyBrightnessContrast,
    applySharpening,
    detectDocumentCorners,
    flattenPerspective,
    getOutputDimensions,
    HANDLE_RADIUS,
} from './flattenDocumentEngine';

const MAX_FILE_BYTES = 50 * 1024 * 1024;
const OUTPUT_FORMATS = ['jpg', 'png', 'webp', 'avif'];

function formatExt(mime) {
    if (mime === 'image/jpeg') return 'jpg';
    if (mime === 'image/png') return 'png';
    if (mime === 'image/webp') return 'webp';
    if (mime === 'image/avif') return 'avif';
    return 'jpg';
}

function mimeFromFormat(format) {
    if (format === 'jpg') return 'image/jpeg';
    if (format === 'png') return 'image/png';
    if (format === 'webp') return 'image/webp';
    if (format === 'avif') return 'image/avif';
    return 'image/jpeg';
}

const FORMAT_ICONS = {
    jpg: IconFileTypeJpg,
    png: IconFileTypePng,
    webp: IconPhoto,
    avif: IconPhotoScan,
};

function segBtnClass(active) {
    return `flex flex-1 min-h-[54px] flex-col items-center justify-center gap-1 rounded-lg border-none px-1 py-1.5 text-[11px] leading-tight transition-all disabled:cursor-not-allowed disabled:opacity-45 ${
        active
            ? 'bg-white font-semibold text-amber-600 shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-900 dark:text-amber-400'
            : 'bg-transparent font-medium text-[#6B635C] dark:text-zinc-400'
    }`;
}

const PREVIEW_PATTERN_BG = {
    backgroundColor: '#EBEBEF',
    backgroundImage: 'radial-gradient(circle, #C4C4CB 1px, transparent 1px)',
    backgroundSize: '14px 14px',
};

function canvasToBlob(canvas, mime, quality) {
    return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), mime, quality ?? undefined);
    });
}

function loadImageFromBlob(blob) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = reject;
        img.src = url;
    });
}

async function blobToCanvas(blob) {
    const img = await loadImageFromBlob(blob);
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.getContext('2d').drawImage(img, 0, 0);
    return canvas;
}

function encodeQualityForFormat(format) {
    return format === 'png' ? undefined : 0.92;
}

async function encodeFlattenedOutput(sourceCanvas, adjustments, mime) {
    const work = document.createElement('canvas');
    work.width = sourceCanvas.width;
    work.height = sourceCanvas.height;
    work.getContext('2d').drawImage(sourceCanvas, 0, 0);

    const sharpCanvas = applySharpening(work, adjustments.sharpness / 67);
    const finalCanvas = applyBrightnessContrast(sharpCanvas, adjustments.brightness, adjustments.contrast);

    // Shared encoder: AVIF is written in WebAssembly where the canvas cannot.
    const format = formatExt(mime);
    const { blob } = await encodeCanvas(finalCanvas, format, { quality: encodeQualityForFormat(format) ?? 0.92 });
    return blob;
}

async function normalizeInputImage(file) {
    // Keep behavior consistent with existing flatten tools (HEIC/HEIF -> JPEG).
    const name = file?.name?.toLowerCase() || '';
    const isHeic = /\.(heic|heif)$/.test(name) || file.type === 'image/heic' || file.type === 'image/heif';
    if (!isHeic) return file;

    const heic2any = (await import('heic2any')).default;
    const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
    const blob = Array.isArray(converted) ? converted[0] : converted;
    const base = file.name.replace(/\.(heic|heif)$/i, '');
    return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
}

export default function FlattenImagePerspectiveTool() {
    const { t } = useTranslation();

    const fileInputRef = useRef(null);
    const canvasRef = useRef(null);
    const imgRef = useRef(null);
    const imgUrlRef = useRef('');
    const previewRef = useRef(null);
    const croppedCanvasRef = useRef(null);
    const previewEncodeRef = useRef(0);

    // Stage: crop -> adjust
    const [stage, setStage] = useState('crop');
    const [file, setFile] = useState(null);
    const [imgUrl, setImgUrl] = useState('');
    const [naturalSize, setNaturalSize] = useState(null);
    const [canvasSize, setCanvasSize] = useState(null);
    const [canvasScale, setCanvasScale] = useState(1);
    const [corners, setCorners] = useState(null);
    const [dragging, setDragging] = useState(null); // 0..3 or "move"
    const [moveDragStart, setMoveDragStart] = useState(null);
    const [moveDragBaseCorners, setMoveDragBaseCorners] = useState(null);
    const [cropZoom, setCropZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const detectionDoneRef = useRef(false);

    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);

    // Adjust stage
    const [previewBlob, setPreviewBlob] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');

    const [resultSharpness, setResultSharpness] = useState(0);
    const [brightness, setBrightness] = useState(100);
    const [contrast, setContrast] = useState(100);
    // Before/after lightbox, opened from the eye on the preview.
    const [compareOpen, setCompareOpen] = useState(false);

    const [zoom, setZoom] = useState(0.75);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [scrollAreaHeight, setScrollAreaHeight] = useState(640);
    const [isAdjusting, setIsAdjusting] = useState(false);

    const [format, setFormat] = useState('jpg'); // for final download
    const outputFormatMime = useMemo(() => mimeFromFormat(format), [format]);

    const status = deriveToolStatus({
        hasInput: !!file,
        canRun: stage === 'crop'
            ? !!file && !!corners && !processing
            : !!previewBlob && !processing,
        processing,
        hasResult: stage === 'adjust' && !!previewBlob,
        hasError: !!error,
    });

    const statusText = getToolStatusText(status, t, {
        ready: t('toolPage.statusText.ready'),
        success: t('flattenImage.done'),
        error: error || t('toolPage.statusText.error'),
    });

    const invalidType = useCallback((msgKey) => {
        const msg = t(msgKey);
        setError(msg);
        notifications.show({ title: t('common.error'), message: msg, color: 'red' });
    }, [t]);

    const handlePickFile = useCallback(async (incomingFiles) => {
        const selected = Array.from(incomingFiles || [])[0] ?? null;
        if (!selected) return;
        const name = (selected.name || '').toLowerCase();
        const okType = (selected.type || '').startsWith('image/');
        const okExt = /\.(heic|heif|avif|tiff?)$/.test(name);
        if (!okType && !okExt) {
            invalidType('flattenImage.errors.invalidType');
            return;
        }
        if (selected.size > MAX_FILE_BYTES) {
            invalidType('flattenImage.errors.tooLarge');
            return;
        }

        try {
            setProcessing(true);
            setError(null);
            setStage('crop');
            setPreviewBlob(null);
            croppedCanvasRef.current = null;

            const normalized = await normalizeInputImage(selected);
            setFile(normalized);
        } catch {
            invalidType('flattenImage.errors.failed');
        } finally {
            setProcessing(false);
        }
    }, [invalidType]);

    // Ctrl/Cmd+V anywhere on the page drops a copied picture straight in.
    useImagePaste(handlePickFile, { enabled: !processing });

    const handleReset = useCallback(() => {
        if (imgUrlRef.current) URL.revokeObjectURL(imgUrlRef.current);
        imgUrlRef.current = '';
        if (previewUrl) URL.revokeObjectURL(previewUrl);

        croppedCanvasRef.current = null;
        previewEncodeRef.current += 1;

        setStage('crop');
        setFile(null);
        setImgUrl('');
        setNaturalSize(null);
        setCanvasSize(null);
        setCorners(null);
        setDragging(null);
        setMoveDragStart(null);
        setMoveDragBaseCorners(null);
        setCropZoom(1);
        setRotation(0);

        setPreviewBlob(null);
        setPreviewUrl('');

        setResultSharpness(0);
        setBrightness(100);
        setContrast(100);
        setCompareOpen(false);
        setZoom(0.75);
        setIsFullscreen(false);
        setIsAdjusting(false);
        setError(null);
        setFormat('jpg');
    }, [previewUrl]);

    // Load image when file changes.
    useEffect(() => {
        detectionDoneRef.current = false;
        if (!file) {
            if (imgUrlRef.current) URL.revokeObjectURL(imgUrlRef.current);
            imgUrlRef.current = '';
            if (imgUrl) URL.revokeObjectURL(imgUrl);
            setImgUrl('');
            setNaturalSize(null);
            setCanvasSize(null);
            setCorners(null);
            imgRef.current = null;
            return;
        }

        // HEIC phone shots are decoded first: the corner editor draws the file
        // through an <img>, which paints nothing for raw HEIC outside Safari.
        let cancelled = false;
        toDisplayableBlob(file).then((displayable) => {
            if (cancelled) return;
            const url = URL.createObjectURL(displayable);
            imgUrlRef.current = url;
            setImgUrl(url);

            const img = new Image();
            img.onload = () => {
                if (cancelled) return;
                imgRef.current = img;
                setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
                setRotation(0);
            };
            img.onerror = () => notifications.show({ title: t('common.error'), message: t('flattenImage.errors.failed'), color: 'red' });
            img.src = url;
        });

        return () => {
            cancelled = true;
            if (imgUrlRef.current) URL.revokeObjectURL(imgUrlRef.current);
            imgUrlRef.current = '';
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file]);

    // Recompute canvas size + auto-detect corners.
    useEffect(() => {
        if (!naturalSize) return;

        const nw = naturalSize.width;
        const nh = naturalSize.height;
        const rad = (rotation * Math.PI) / 180;
        const effW = Math.ceil(Math.abs(nw * Math.cos(rad)) + Math.abs(nh * Math.sin(rad)));
        const effH = Math.ceil(Math.abs(nw * Math.sin(rad)) + Math.abs(nh * Math.cos(rad)));

        const MAX_CANVAS_W = 800;
        const MAX_CANVAS_H = 600;
        const cs = Math.min(1, MAX_CANVAS_W / effW, MAX_CANVAS_H / effH);
        const cw = Math.round(effW * cs);
        const ch = Math.round(effH * cs);

        setCanvasScale(cs);
        setCanvasSize({ width: cw, height: ch });

        if (rotation === 0 && imgRef.current && !detectionDoneRef.current) {
            detectionDoneRef.current = true;
            try {
                const detected = detectDocumentCorners(imgRef.current, nw, nh);
                if (detected) {
                    setCorners(detected.map((c) => ({
                        x: Math.max(0, Math.min(cw, Math.round(c.x * cs))),
                        y: Math.max(0, Math.min(ch, Math.round(c.y * cs))),
                    })));
                    return;
                }
            } catch {
                // fallthrough to defaults
            }
        }

        setCorners([
            { x: cw * 0.1, y: ch * 0.1 },
            { x: cw * 0.9, y: ch * 0.1 },
            { x: cw * 0.9, y: ch * 0.9 },
            { x: cw * 0.1, y: ch * 0.9 },
        ]);
    }, [naturalSize, rotation]);

    // Draw crop canvas (re-run when returning from adjust stage — canvas remounts).
    useLayoutEffect(() => {
        if (stage !== 'crop' || !canvasRef.current || !imgRef.current || !corners || !canvasSize || !naturalSize) return;

        const canvas = canvasRef.current;
        canvas.width = canvasSize.width;
        canvas.height = canvasSize.height;

        const ctx = canvas.getContext('2d');
        const cw = canvas.width;
        const ch = canvas.height;
        const nw = naturalSize.width;
        const nh = naturalSize.height;

        const drawRotatedImage = () => {
            ctx.save();
            ctx.translate(cw / 2, ch / 2);
            ctx.rotate((rotation * Math.PI) / 180);
            ctx.drawImage(imgRef.current, -(nw * canvasScale) / 2, -(nh * canvasScale) / 2, nw * canvasScale, nh * canvasScale);
            ctx.restore();
        };

        ctx.clearRect(0, 0, cw, ch);
        drawRotatedImage();

        // Dim outside polygon.
        ctx.save();
        ctx.fillStyle = 'rgba(0,0,0,0.45)';
        ctx.fillRect(0, 0, cw, ch);
        ctx.globalCompositeOperation = 'destination-out';

        ctx.beginPath();
        ctx.moveTo(corners[0].x, corners[0].y);
        corners.slice(1).forEach((p) => ctx.lineTo(p.x, p.y));
        ctx.closePath();
        ctx.fill();
        ctx.restore();

        // Clip inside polygon then redraw the image.
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(corners[0].x, corners[0].y);
        corners.slice(1).forEach((p) => ctx.lineTo(p.x, p.y));
        ctx.closePath();
        ctx.clip();
        drawRotatedImage();
        ctx.restore();

        // Polygon border.
        ctx.beginPath();
        ctx.moveTo(corners[0].x, corners[0].y);
        corners.slice(1).forEach((p) => ctx.lineTo(p.x, p.y));
        ctx.closePath();
        ctx.strokeStyle = 'rgba(255,255,255,0.9)';
        ctx.lineWidth = 2;
        ctx.setLineDash([8, 4]);
        ctx.stroke();
        ctx.setLineDash([]);

        const CORNER_COLORS = ['#4ECDC4', '#845EF7', '#FF6B6B', '#FFD43B'];
        corners.forEach((p, i) => {
            const isActive = dragging === i;
            const r = isActive ? HANDLE_RADIUS + 3 : HANDLE_RADIUS;

            ctx.shadowColor = 'rgba(0,0,0,0.5)';
            ctx.shadowBlur = 6;
            ctx.beginPath();
            ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
            ctx.fillStyle = 'white';
            ctx.fill();
            ctx.strokeStyle = CORNER_COLORS[i];
            ctx.lineWidth = isActive ? 4 : 3;
            ctx.stroke();
            ctx.shadowBlur = 0;

            ctx.beginPath();
            ctx.arc(p.x, p.y, 3, 0, Math.PI * 2);
            ctx.fillStyle = CORNER_COLORS[i];
            ctx.fill();
        });
    }, [corners, canvasSize, dragging, rotation, naturalSize, canvasScale, stage]);

    // Helpers: canvas coordinates.
    const getCanvasPos = useCallback((e) => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        return {
            x: (clientX - rect.left) * (canvas.width / rect.width),
            y: (clientY - rect.top) * (canvas.height / rect.height),
        };
    }, []);

    const isPointInPolygon = useCallback((point, polygon) => {
        if (!point || !polygon?.length) return false;
        let inside = false;
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
            const xi = polygon[i].x;
            const yi = polygon[i].y;
            const xj = polygon[j].x;
            const yj = polygon[j].y;
            const intersect = yi > point.y !== yj > point.y
                && point.x < ((xj - xi) * (point.y - yi)) / ((yj - yi) || 1e-8) + xi;
            if (intersect) inside = !inside;
        }
        return inside;
    }, []);

    const handleMouseDown = useCallback((e) => {
        if (!corners) return;
        e.preventDefault();
        const pos = getCanvasPos(e);
        if (!pos) return;

        for (let i = 0; i < 4; i++) {
            if (Math.hypot(pos.x - corners[i].x, pos.y - corners[i].y) <= HANDLE_RADIUS + 8) {
                setDragging(i);
                return;
            }
        }

        if (isPointInPolygon(pos, corners)) {
            setDragging('move');
            setMoveDragStart(pos);
            setMoveDragBaseCorners(corners);
        }
    }, [corners, getCanvasPos, isPointInPolygon]);

    const handleMouseMove = useCallback((e) => {
        if (dragging === null || !canvasSize) return;
        e.preventDefault();
        const pos = getCanvasPos(e);
        if (!pos) return;

        if (dragging === 'move') {
            if (!moveDragStart || !moveDragBaseCorners) return;
            const dxRaw = pos.x - moveDragStart.x;
            const dyRaw = pos.y - moveDragStart.y;

            const minX = Math.min(...moveDragBaseCorners.map((p) => p.x));
            const maxX = Math.max(...moveDragBaseCorners.map((p) => p.x));
            const minY = Math.min(...moveDragBaseCorners.map((p) => p.y));
            const maxY = Math.max(...moveDragBaseCorners.map((p) => p.y));

            const dx = Math.max(-minX, Math.min(canvasSize.width - maxX, dxRaw));
            const dy = Math.max(-minY, Math.min(canvasSize.height - maxY, dyRaw));

            setCorners(moveDragBaseCorners.map((p) => ({ x: p.x + dx, y: p.y + dy })));
            return;
        }

        const nx = Math.max(0, Math.min(canvasSize.width, pos.x));
        const ny = Math.max(0, Math.min(canvasSize.height, pos.y));

        setCorners((prev) => {
            const next = [...prev];
            next[dragging] = { x: nx, y: ny };
            return next;
        });
    }, [dragging, canvasSize, getCanvasPos, moveDragStart, moveDragBaseCorners]);

    const handleMouseUp = useCallback(() => {
        setDragging(null);
        setMoveDragStart(null);
        setMoveDragBaseCorners(null);
    }, []);

    const handleAutoDetect = useCallback(() => {
        if (!imgRef.current || !naturalSize || !canvasSize || !canvasScale) return;
        try {
            const detected = detectDocumentCorners(imgRef.current, naturalSize.width, naturalSize.height);
            if (!detected) {
                notifications.show({ title: 'শনাক্ত করা যায়নি', message: 'ডকুমেন্টের প্রান্ত খুঁজে পাওয়া যায়নি। ম্যানুয়ালি ঠিক করুন।', color: 'yellow' });
                return;
            }

            const { width: cw, height: ch } = canvasSize;
            setCorners(detected.map((c) => ({
                x: Math.max(0, Math.min(cw, Math.round(c.x * canvasScale))),
                y: Math.max(0, Math.min(ch, Math.round(c.y * canvasScale))),
            })));
            notifications.show({ title: 'ডকুমেন্ট শনাক্ত হয়েছে', message: 'কর্নার পুনরায় ডকুমেন্টের প্রান্তে সেট হয়েছে।', color: 'teal', autoClose: 3000 });
        } catch {
            notifications.show({ title: 'শনাক্তকরণ ত্রুটি', message: 'ডকুমেন্ট শনাক্ত করা যায়নি।', color: 'orange', autoClose: 4000 });
        }
    }, [canvasScale, canvasSize, naturalSize]);

    const handleResetCorners = useCallback(() => {
        setCropZoom(1);
        if (rotation !== 0) {
            setRotation(0);
            return;
        }
        if (!canvasSize) return;
        const { width: cw, height: ch } = canvasSize;
        setCorners([
            { x: cw * 0.1, y: ch * 0.1 },
            { x: cw * 0.9, y: ch * 0.1 },
            { x: cw * 0.9, y: ch * 0.9 },
            { x: cw * 0.1, y: ch * 0.9 },
        ]);
    }, [canvasSize, rotation]);

    const outDims = useMemo(() => {
        if (!corners) return null;
        const naturalCorners = corners.map((c) => ({ x: c.x / canvasScale, y: c.y / canvasScale }));
        return getOutputDimensions(naturalCorners);
    }, [corners, canvasScale]);

    // Create preview blob from crop stage.
    const handleApplyCrop = useCallback(async () => {
        if (!corners || !canvasScale || !imgUrl || !naturalSize || !file) return;
        setProcessing(true);
        try {
            const naturalCorners = corners.map((c) => ({ x: c.x / canvasScale, y: c.y / canvasScale }));
            const { width: outW, height: outH } = getOutputDimensions(naturalCorners);

            const flattenedBlob = await flattenPerspective(imgUrl, naturalSize, rotation, naturalCorners, outW, outH, 'image/png');
            if (!flattenedBlob) throw new Error('output failed');

            const canvas = await blobToCanvas(flattenedBlob);
            croppedCanvasRef.current = canvas;

            const adjustments = { sharpness: 0, brightness: 100, contrast: 100 };
            const encoded = await encodeFlattenedOutput(canvas, adjustments, outputFormatMime);
            if (!encoded) throw new Error('output failed');

            const baseName = file.name.replace(/\.[^/.]+$/, '');
            const ext = formatExt(outputFormatMime);
            setPreviewBlob(new File([encoded], `${baseName}-flattened.${ext}`, { type: outputFormatMime }));
            setResultSharpness(0);
            setBrightness(100);
            setContrast(100);
            setCompareOpen(false);
            setZoom(1);
            setStage('adjust');
        } catch (err) {
            const msg = err?.message || 'ক্রপ প্রয়োগ করা যায়নি';
            notifications.show({ title: 'ত্রুটি', message: msg, color: 'red' });
        } finally {
            setProcessing(false);
        }
    }, [canvasScale, corners, file, imgUrl, naturalSize, outputFormatMime, rotation]);

    // previewUrl from previewBlob
    useEffect(() => {
        if (!previewBlob) {
            setPreviewUrl('');
            return;
        }
        const url = URL.createObjectURL(previewBlob);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [previewBlob]);

    // Re-encode preview when adjustments or output format change.
    useEffect(() => {
        if (!croppedCanvasRef.current || stage !== 'adjust') return undefined;

        const jobId = ++previewEncodeRef.current;
        let cancelled = false;

        (async () => {
            setIsAdjusting(true);
            try {
                const blob = await encodeFlattenedOutput(
                    croppedCanvasRef.current,
                    { sharpness: resultSharpness, brightness, contrast },
                    outputFormatMime,
                );
                if (cancelled || jobId !== previewEncodeRef.current || !blob) return;

                const baseName = file?.name.replace(/\.[^/.]+$/, '') ?? 'flattened';
                const ext = formatExt(outputFormatMime);
                setPreviewBlob(new File([blob], `${baseName}-flattened.${ext}`, { type: outputFormatMime }));
            } finally {
                if (jobId === previewEncodeRef.current) setIsAdjusting(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [resultSharpness, brightness, contrast, outputFormatMime, file]);

    // Fullscreen listener
    useEffect(() => {
        const handler = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handler);
        return () => document.removeEventListener('fullscreenchange', handler);
    }, []);

    // Measure scroll area height (for maxHeight constraint)
    useEffect(() => {
        const el = previewRef.current;
        if (!el || stage !== 'adjust') return undefined;

        const ro = new ResizeObserver((entries) => {
            setScrollAreaHeight(entries[0].contentRect.height);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [stage]);

    const handleFullscreen = useCallback(async () => {
        if (!previewRef.current) return;
        if (document.fullscreenElement) await document.exitFullscreen();
        else await previewRef.current.requestFullscreen();
    }, []);

    const displayUrl = previewUrl;
    // Nothing to reset until a slider has moved off its default.
    const adjustmentsChanged = resultSharpness !== 0 || brightness !== 100 || contrast !== 100;

    const compareImages = useMemo(() => (imgUrl && previewUrl ? [{
        id: 0,
        fileName: previewBlob?.name || file?.name || 'flattened',
        mimeType: previewBlob?.type || 'image/jpeg',
        aiImage: previewUrl,
        // What the shared modal calls the "before" pane.
        uploadedImage: imgUrl,
    }] : []), [imgUrl, previewUrl, previewBlob, file]);

    const handleDownload = useCallback(() => {
        if (!previewBlob) return;
        downloadBlob(previewBlob, previewBlob.name);
    }, [previewBlob]);

    const handlePrint = useCallback(() => {
        if (!displayUrl) return;
        recordToolUsage('image/flatten', { category: 'image' });
        const win = window.open('', '_blank');
        if (!win) return;
        win.document.write(`
            <html>
              <head>
                <title>${t('flattenImage.print')}</title>
                <style>
                  body{margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#fff}
                  img{max-width:100%;max-height:100vh;object-fit:contain}
                  @media print{body{margin:0}}
                </style>
              </head>
              <body>
                <img src="${displayUrl}" onload="window.print();window.close()"/>
              </body>
            </html>
        `);
        win.document.close();
    }, [displayUrl, t]);

    const processingLabel = stage === 'crop'
        ? t('flattenImage.flattening')
        : t('flattenImage.applyingAdjustments');

    const runFlatten = useCallback(() => {
        recordToolUsage('image/flatten', { category: 'image' });
        handleApplyCrop();
    }, [handleApplyCrop]);

    return (
        <>
        <ToolPageLayout
            slug="image/flatten"
            subtitleKey="flattenImage.subtitle"
            status={status}
            statusText={stage === 'adjust' && previewBlob && !processing && outDims
                ? `${statusText} · ${toBnDigits(outDims.width)} × ${toBnDigits(outDims.height)} px`
                : statusText}
            onReset={file || error ? handleReset : undefined}
            primaryAction={stage === 'adjust' && previewBlob && !processing ? {
                label: t('flattenImage.download'),
                onClick: handleDownload,
                icon: IconDownload,
            } : {
                label: t('flattenImage.flatten'),
                onClick: runFlatten,
                disabled: !file || !corners || processing,
                loading: processing,
            }}
            secondaryAction={stage === 'adjust' && previewBlob && !processing ? [
                {
                    key: 'print',
                    label: t('flattenImage.print'),
                    onClick: handlePrint,
                    disabled: !displayUrl,
                    icon: IconPrinter,
                },
                // The corners are still there — going back re-opens the same selection
                // instead of starting over.
                {
                    key: 'back-to-crop',
                    label: t('flattenImage.backToCrop'),
                    onClick: () => {
                        setCompareOpen(false);
                        setCropZoom(1);
                        setStage('crop');
                    },
                    icon: IconArrowLeft,
                },
            ] : undefined}
            previewFirst
            showTrust={false}
            settings={(
                <div className="flex flex-col gap-3">
                    {/* The picture is chosen in the preview panel, so the left
                        column carries settings only — this input is what the
                        empty state opens. */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,.heic,.heif,.avif,.tif,.tiff"
                        multiple={false}
                        className="hidden"
                        onChange={(e) => { handlePickFile(e.target.files); e.target.value = ''; }}
                    />

                    <OutputFormatPicker
                        value={format}
                        onChange={setFormat}
                        formats={OUTPUT_FORMATS}
                        disabled={processing}
                    />

                    {stage === 'crop' ? (
                        <ToolCard title={t('flattenImage.rotationTitle')}>
                            <div className="space-y-3">
                                <div className="flex items-center justify-between gap-3">
                                    <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                        {t('flattenImage.rotationLabel')}
                                    </span>
                                    <span className="text-[12.5px] font-semibold text-amber-600">{toBnDigits(rotation)}°</span>
                                </div>
                                <input
                                    type="range"
                                    min={-180}
                                    max={180}
                                    step={1}
                                    value={rotation}
                                    onChange={(e) => setRotation(Number(e.target.value))}
                                    className="w-full accent-amber-500"
                                />
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={handleAutoDetect}
                                        disabled={!file || processing}
                                        className="flex-1 rounded-xl border border-[#ECE8E4] bg-white px-3 py-2 text-xs font-semibold text-[#3D3833] transition-colors hover:border-amber-400/60 disabled:cursor-not-allowed disabled:opacity-45 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                                    >
                                        {t('flattenImage.autoDetect')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleResetCorners}
                                        disabled={!file || processing}
                                        className="flex-1 rounded-xl border border-[#ECE8E4] bg-white px-3 py-2 text-xs font-semibold text-[#3D3833] transition-colors hover:border-amber-400/60 disabled:cursor-not-allowed disabled:opacity-45 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                                    >
                                        {t('flattenImage.resetCorners')}
                                    </button>
                                </div>
                                {outDims ? (
                                    <div className="rounded-xl border border-[#ECE8E4] bg-[#FAFAF9] p-3 dark:border-zinc-700 dark:bg-zinc-800/50">
                                        <div className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                            {t('flattenImage.outputTitle')}
                                        </div>
                                        <div className="mt-1 text-[12px] text-[#A8A099] dark:text-zinc-500">
                                            {toBnDigits(outDims.width)} × {toBnDigits(outDims.height)} px
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        </ToolCard>
                    ) : (
                        <ToolCard
                            title={t('flattenImage.adjustTitle')}
                            headerAction={(
                                <Hint label={t('flattenImage.resetAdjustments')}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setResultSharpness(0);
                                            setBrightness(100);
                                            setContrast(100);
                                        }}
                                        disabled={!adjustmentsChanged}
                                        aria-label={t('flattenImage.resetAdjustments')}
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[#ECE8E4] bg-white text-[#6B635C] transition-colors hover:border-amber-400/40 hover:text-amber-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-[#ECE8E4] disabled:hover:text-[#6B635C] dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400"
                                    >
                                        <IconRestore size={16} stroke={1.9} />
                                    </button>
                                </Hint>
                            )}
                        >
                            <div className="space-y-4">
                                <div>
                                    <div className="mb-2 flex items-center justify-between">
                                        <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                            {t('flattenImage.sharpness')}
                                        </span>
                                        <span className="text-[12.5px] font-semibold text-amber-600">{toBnDigits(resultSharpness)}</span>
                                    </div>
                                    <input
                                        type="range"
                                        min={0}
                                        max={100}
                                        step={1}
                                        value={resultSharpness}
                                        onChange={(e) => setResultSharpness(Number(e.target.value))}
                                        className="w-full accent-amber-500"
                                    />
                                </div>

                                <div>
                                    <div className="mb-2 flex items-center justify-between">
                                        <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                            {t('flattenImage.brightness')}
                                        </span>
                                        <span className="text-[12.5px] font-semibold text-amber-600">{toBnDigits(brightness)}%</span>
                                    </div>
                                    <input
                                        type="range"
                                        min={50}
                                        max={150}
                                        step={1}
                                        value={brightness}
                                        onChange={(e) => setBrightness(Number(e.target.value))}
                                        className="w-full accent-amber-500"
                                    />
                                </div>

                                <div>
                                    <div className="mb-2 flex items-center justify-between">
                                        <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                            {t('flattenImage.contrast')}
                                        </span>
                                        <span className="text-[12.5px] font-semibold text-amber-600">{toBnDigits(contrast)}%</span>
                                    </div>
                                    <input
                                        type="range"
                                        min={50}
                                        max={150}
                                        step={1}
                                        value={contrast}
                                        onChange={(e) => setContrast(Number(e.target.value))}
                                        className="w-full accent-amber-500"
                                    />
                                </div>
                            </div>
                        </ToolCard>
                    )}
                </div>
            )}
            preview={(
                <ToolPreviewPanel
                    title={t('flattenImage.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('flattenImage.previewEmpty')}
                    idleHint={t('flattenImage.previewEmptyHint')}
                    processingLabel={processing ? processingLabel : undefined}
                    error={error}
                    canvasTone="plain"
                    // The page stays visible through a frosted sheet while the
                    // warp runs, so the wait shows the work.
                    processingTone="veil"
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={(e) => { e.preventDefault(); handlePickFile(e.dataTransfer.files); }}
                    onIdleDragOver={(e) => { e.preventDefault(); }}
                    onIdleDragLeave={() => {}}
                    idleDragging={false}
                    showPasteHint
                    showIdle={!file && !error}
                >
                    {file ? (
                        stage === 'crop' ? (
                            <div className="flex h-full min-h-0 flex-col gap-3">
                                <div className="flex flex-none flex-wrap items-center justify-between gap-3">
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setCropZoom((z) => Math.max(0.25, parseFloat((z - 0.25).toFixed(2))))}
                                            disabled={cropZoom <= 0.25}
                                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[#ECE8E4] bg-white hover:border-amber-400/40 disabled:opacity-40 dark:border-zinc-700 dark:bg-zinc-900"
                                        >
                                            <IconMinus size={16} stroke={2} />
                                        </button>
                                        <div className="text-sm font-semibold text-[#3D3833] dark:text-zinc-200" style={{ minWidth: 54, textAlign: 'center' }}>
                                            {Math.round(cropZoom * 100)}%
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setCropZoom((z) => Math.min(3, parseFloat((z + 0.25).toFixed(2))))}
                                            disabled={cropZoom >= 3}
                                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[#ECE8E4] bg-white hover:border-amber-400/40 disabled:opacity-40 dark:border-zinc-700 dark:bg-zinc-900"
                                        >
                                            <IconPlus size={16} stroke={2} />
                                        </button>
                                    </div>
                                    <button
                                        type="button"
                                        className="rounded-xl border border-[#ECE8E4] bg-white px-3 py-2 text-xs font-semibold text-[#6B635C] hover:border-amber-400/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400"
                                        onClick={() => setCropZoom(1)}
                                    >
                                        {t('flattenImage.fit')}
                                    </button>
                                </div>

                                <div
                                    className="flex min-h-0 flex-1 overflow-auto rounded-xl border border-[#ECE8E4] bg-[#F7F5F3] p-2 dark:border-zinc-700 dark:bg-zinc-800/50"
                                    style={PREVIEW_PATTERN_BG}
                                >
                                    <div style={{ width: 'max-content', minWidth: '100%', minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12, boxSizing: 'border-box' }}>
                                        <canvas
                                            ref={canvasRef}
                                            style={{
                                                display: 'block',
                                                maxWidth: cropZoom <= 1 ? '100%' : 'none',
                                                height: 'auto',
                                                zoom: cropZoom,
                                            }}
                                            onMouseDown={handleMouseDown}
                                            onMouseMove={handleMouseMove}
                                            onMouseUp={handleMouseUp}
                                            onMouseLeave={handleMouseUp}
                                            onTouchStart={handleMouseDown}
                                            onTouchMove={handleMouseMove}
                                            onTouchEnd={handleMouseUp}
                                        />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="flex h-full min-h-0 flex-col gap-3">
                                <div className="flex flex-none items-center justify-between rounded-xl border border-[#ECE8E4] bg-white px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900">
                                    <div className="flex items-center gap-3">
                                        <button
                                            type="button"
                                            onClick={() => setZoom((z) => Math.max(0.25, parseFloat((z - 0.25).toFixed(2))))}
                                            disabled={zoom <= 0.25}
                                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[#ECE8E4] bg-white hover:border-amber-400/40 disabled:opacity-40 dark:border-zinc-700 dark:bg-zinc-900"
                                        >
                                            <IconMinus size={14} stroke={2.2} />
                                        </button>
                                        <div className="text-sm font-semibold text-[#3D3833] dark:text-zinc-200" style={{ minWidth: 52, textAlign: 'center' }}>
                                            {Math.round(zoom * 100)}%
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setZoom((z) => Math.min(2, parseFloat((z + 0.25).toFixed(2))))}
                                            disabled={zoom >= 2}
                                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[#ECE8E4] bg-white hover:border-amber-400/40 disabled:opacity-40 dark:border-zinc-700 dark:bg-zinc-900"
                                        >
                                            <IconPlus size={14} stroke={2.2} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setZoom(1)}
                                            className="ml-1 rounded-lg px-2.5 py-1 text-xs font-semibold text-[#6B635C] hover:border-amber-400/40 dark:text-zinc-400"
                                        >
                                            {t('flattenImage.fit')}
                                        </button>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        {isAdjusting ? (
                                            <div className="text-xs text-[#A8A099] dark:text-zinc-400">
                                                {t('flattenImage.applying')}
                                            </div>
                                        ) : null}
                                        <button
                                            type="button"
                                            onClick={handleFullscreen}
                                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[#ECE8E4] bg-white hover:border-amber-400/40 dark:border-zinc-700 dark:bg-zinc-900"
                                            title={isFullscreen ? t('flattenImage.exitFullscreen') : t('flattenImage.fullscreen')}
                                        >
                                            {isFullscreen ? <IconArrowsMinimize size={16} /> : <IconArrowsMaximize size={16} />}
                                        </button>
                                    </div>
                                </div>

                                <div
                                    ref={previewRef}
                                    className="flex min-h-0 flex-1 overflow-auto rounded-xl border border-[#ECE8E4] bg-[#F7F5F3] p-2 dark:border-zinc-700 dark:bg-zinc-800/50"
                                    style={PREVIEW_PATTERN_BG}
                                >
                                    <div style={{ width: 'max-content', minWidth: '100%', minHeight: `${scrollAreaHeight}px`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, boxSizing: 'border-box' }}>
                                        {displayUrl ? (
                                            <div className="group relative" style={{ zoom }}>
                                                <img
                                                    src={displayUrl}
                                                    alt="Flatten preview"
                                                    style={{
                                                        display: 'block',
                                                        maxWidth: zoom <= 1 ? '100%' : 'none',
                                                        maxHeight: zoom <= 1 ? Math.max(100, scrollAreaHeight - 32) + 'px' : 'none',
                                                        width: 'auto',
                                                        height: 'auto',
                                                        borderRadius: 4,
                                                    }}
                                                />
                                                {/* On hover: opens the before/after lightbox. */}
                                                {imgUrl ? (
                                                    <div className="absolute inset-0 flex items-center justify-center rounded bg-black/0 opacity-0 transition-all duration-150 group-hover:bg-black/25 group-hover:opacity-100">
                                                        <Hint label={t('flattenImage.compare')}>
                                                            <button
                                                                type="button"
                                                                onClick={() => setCompareOpen(true)}
                                                                aria-label={t('flattenImage.compare')}
                                                                className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#1C1815] shadow-lg transition-transform hover:scale-105"
                                                                style={{ zoom: 1 / zoom }}
                                                            >
                                                                <IconEye size={22} stroke={1.9} />
                                                            </button>
                                                        </Hint>
                                                    </div>
                                                ) : null}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        )
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
        <ImagePreviewModal
            opened={compareOpen}
            onClose={() => setCompareOpen(false)}
            images={compareImages}
            initialImage={compareImages[0] ?? null}
            onDownloadImage={(img, adjusted) => {
                if (previewBlob) downloadBlob(adjusted || previewBlob, previewBlob.name);
            }}
            initialViewMode="side-by-side"
            useImageTitle
            hideEdit
            hidePrint
            hideShare
            hideCustomDownload
            hideDelete
        />
        </>
    );
}

